import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Routes from '../configuration/routes/RouteList';
import signUp from '../api/actions/signUp';
import type { ApiError } from '../functions/APIClient';
import {
  getEnabledSupabaseProviders,
  isSupabaseAuthConfigured,
  signInWithSupabaseProvider,
  signInWithSupabase,
  signUpWithSupabase,
} from '../lib/supabaseAuth';

const Register = () => {
  const navigate = useNavigate();
  const useSupabaseAuth = isSupabaseAuthConfigured();
  const enabledProviders = getEnabledSupabaseProviders();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSocialSignIn = async (provider: 'google' | 'apple') => {
    setErrorMessage(null);
    setSuccessMessage(null);
    const redirectTo = `${window.location.origin}${Routes.AuthCallback}`;
    const { error } = await signInWithSupabaseProvider(provider, redirectTo);
    if (error) {
      setErrorMessage(error.message || `Failed to start ${provider} sign-in.`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (password !== confirmPassword) {
      setErrorMessage("Passwords don't match.");
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      if (useSupabaseAuth) {
        const signUpResult = await signUpWithSupabase(username, password);
        if (signUpResult.error) {
          setErrorMessage(signUpResult.error.message || 'Registration failed.');
          return;
        }

        if (signUpResult.data.session) {
          navigate(Routes.Home);
          return;
        }

        const signInResult = await signInWithSupabase(username, password);
        if (!signInResult.error) {
          navigate(Routes.Home);
          return;
        }

        setSuccessMessage('Account created. Check your inbox if Supabase asks for email confirmation.');
        return;
      }

      const res = await signUp(username, password, confirmPassword);
      if (res.status === 204) {
        navigate(Routes.Home);
      } else {
        setErrorMessage(res.error?.error ?? 'Registration failed.');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      setErrorMessage(apiErr?.message ?? 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'oklch(0.13 0.008 260)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <title>NM | Create account</title>

      {/* Background glow */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '700px',
          height: '500px',
          background:
            'radial-gradient(ellipse, oklch(0.52 0.22 265 / 0.1) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Brand */}
      <Link
        to={Routes.Landing}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '40px',
          textDecoration: 'none',
        }}
      >
        <span
          style={{
            fontWeight: 800,
            fontSize: '18px',
            letterSpacing: '-0.04em',
            background: 'linear-gradient(135deg, oklch(0.78 0.18 260), oklch(0.65 0.22 300))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          NM
        </span>
        <span
          style={{
            fontSize: '16px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: 'oklch(0.9 0.004 260)',
          }}
        >
          <strong>Niu</strong>
          <span style={{ opacity: 0.55, fontStyle: 'italic' }}>Media</span>
        </span>
      </Link>

      {/* Card */}
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          borderRadius: '16px',
          border: '1px solid oklch(1 0 0 / 0.1)',
          background: 'oklch(0.17 0.008 260)',
          padding: '36px 32px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 32px 64px oklch(0 0 0 / 0.4)',
        }}
      >
        {/* Top edge gradient */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background:
              'linear-gradient(90deg, transparent, oklch(0.52 0.22 265 / 0.5), transparent)',
          }}
        />

        <h1
          style={{
            fontSize: '20px',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: 'oklch(0.97 0.004 260)',
            margin: '0 0 6px',
          }}
        >
          Create your account
        </h1>
        <p style={{ fontSize: '13px', color: 'oklch(0.55 0.01 260)', margin: '0 0 28px' }}>
          {useSupabaseAuth ? 'Create your email login for NiuMedia' : 'Join NiuMedia and start archiving'}
        </p>

        {errorMessage && (
          <div
            style={{
              marginBottom: '20px',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '13px',
              border: '1px solid oklch(0.6 0.2 25)',
              color: 'oklch(0.75 0.18 25)',
              background: 'oklch(0.6 0.2 25 / 0.08)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              marginBottom: '20px',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '13px',
              border: '1px solid oklch(0.62 0.16 150)',
              color: 'oklch(0.82 0.12 150)',
              background: 'oklch(0.62 0.16 150 / 0.08)',
            }}
          >
            {successMessage}
          </div>
        )}

        {useSupabaseAuth && enabledProviders.length > 0 && (
          <>
            {enabledProviders.map(provider => (
              <button
                key={provider}
                type="button"
                onClick={() => handleSocialSignIn(provider)}
                style={{
                  width: '100%',
                  padding: '11px',
                  borderRadius: '8px',
                  border: '1px solid oklch(1 0 0 / 0.12)',
                  background: 'oklch(0.2 0.008 260)',
                  color: 'oklch(0.95 0.004 260)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '14px',
                  fontFamily: 'inherit',
                }}
              >
                Continue with {provider === 'apple' ? 'Apple' : 'Google'}
              </button>
            ))}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '14px',
                color: 'oklch(0.45 0.01 260)',
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: 'oklch(1 0 0 / 0.08)' }} />
              <span>or use email</span>
              <div style={{ flex: 1, height: '1px', background: 'oklch(1 0 0 / 0.08)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {(
            [
              {
                label: 'Username',
                type: 'text',
                id: 'reg_username',
                placeholder: 'your-username',
                autoComplete: 'username',
                value: username,
                onChange: (v: string) => setUsername(v),
              },
              {
                label: 'Password',
                type: 'password',
                id: 'reg_password',
                placeholder: '••••••••',
                autoComplete: 'new-password',
                value: password,
                onChange: (v: string) => setPassword(v),
              },
              {
                label: 'Confirm password',
                type: 'password',
                id: 'reg_confirm',
                placeholder: '••••••••',
                autoComplete: 'new-password',
                value: confirmPassword,
                onChange: (v: string) => setConfirmPassword(v),
              },
            ] as const
          ).map(field => (
            <div key={field.id}>
              <label
                htmlFor={field.id}
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'oklch(0.5 0.01 260)',
                  marginBottom: '6px',
                }}
              >
                {field.id === 'reg_username' && useSupabaseAuth ? 'Email' : field.label}
              </label>
              <input
                id={field.id}
                type={field.id === 'reg_username' && useSupabaseAuth ? 'email' : field.type}
                placeholder={field.id === 'reg_username' && useSupabaseAuth ? 'you@example.com' : field.placeholder}
                autoComplete={field.id === 'reg_username' && useSupabaseAuth ? 'email' : field.autoComplete}
                required
                minLength={field.type === 'password' ? 8 : 1}
                value={field.value}
                onChange={e => field.onChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid oklch(1 0 0 / 0.1)',
                  background: 'oklch(0.22 0.008 260)',
                  color: 'oklch(0.95 0.004 260)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          ))}

          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: '4px',
              width: '100%',
              padding: '11px',
              borderRadius: '8px',
              border: 'none',
              background: loading ? 'oklch(0.45 0.18 265)' : 'oklch(0.52 0.22 265)',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 16px oklch(0.52 0.22 265 / 0.35)',
              fontFamily: 'inherit',
              letterSpacing: '-0.01em',
              transition: 'background 150ms, box-shadow 150ms',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            {loading && (
              <span
                style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  border: '2px solid oklch(1 0 0 / 0.3)',
                  borderTopColor: '#fff',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
      </div>

      <p style={{ marginTop: '20px', fontSize: '13px', color: 'oklch(0.45 0.01 260)' }}>
        Already have an account?{' '}
        <Link
          to={Routes.Login}
          style={{ color: 'oklch(0.72 0.18 265)', textDecoration: 'none', fontWeight: 500 }}
        >
          Sign in
        </Link>
      </p>

      <p
        style={{
          marginTop: '12px',
          fontSize: '11px',
          color: 'oklch(0.38 0.01 260)',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <Link to={Routes.Privacy} style={{ color: 'inherit', textDecoration: 'none' }}>
          Privacy Policy
        </Link>
        <Link to={Routes.Terms} style={{ color: 'inherit', textDecoration: 'none' }}>
          Terms of Service
        </Link>
      </p>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        #reg_username:focus, #reg_password:focus, #reg_confirm:focus {
          border-color: oklch(0.52 0.22 265 / 0.5) !important;
          box-shadow: 0 0 0 3px oklch(0.52 0.22 265 / 0.12);
        }
      `}</style>
    </div>
  );
};

export default Register;
